/**
 * 사진 한 면에서 보이는 원주 구간(가시 호 창). 두 프레임 모델 팩터리는 길이를 반둘레 C/2 로 만든다.
 * 창을 쓰는 함수는 길이가 0 보다 크고 반둘레 이하인 창만 받는다.
 * 프레임 모델 A(둘레 C + 정면 중심)와 B(면별 반둘레)를 같은 모양으로 나타낸다.
 */
export interface ArcWindow {
  readonly circumferenceMm: number;
  readonly startMm: number;
  readonly lengthMm: number;
}

/** 창 시작·끝을 판정할 때 부동소수 오차로 넘친 비율을 끝값으로 보는 허용치(창 길이 대비). */
const FRACTION_EPSILON = 1e-12;

/**
 * value 를 [0, circumference) 로 감는다. 이미 범위 안인 값은 그대로 돌려준다.
 * 나머지 연산은 정확하지만 둘레를 더하는 단계에서 반올림이 생기므로 음수일 때만 더한다.
 * 둘레의 음의 배수와 −0 은 −0 으로 감긴다.
 */
export function wrapMm(value: number, circumferenceMm: number): number {
  const remainder = value % circumferenceMm;
  const wrapped = remainder < 0 ? remainder + circumferenceMm : remainder;
  return wrapped >= circumferenceMm ? 0 : wrapped;
}

/**
 * 둘레·창 시작이 유한수이고 창 길이가 0 보다 크고 반둘레 이하인 창만 계산에 쓴다. 이때 둘레도 0 보다 크다.
 * 정면에서 보이는 호는 반둘레를 넘지 않는다. 둘레에 가까운 창은 끝이 시작과 겹쳐 두 방향 변환이 서로 역이 아니게 된다.
 */
export function isValidArcWindow({ circumferenceMm, startMm, lengthMm }: ArcWindow): boolean {
  return (
    Number.isFinite(circumferenceMm) &&
    Number.isFinite(startMm) &&
    lengthMm > 0 &&
    lengthMm <= circumferenceMm / 2
  );
}

/**
 * 프레임 모델 A: 둘레 C 에서 정면 중심(viewCenterMm) 양옆 C/4 씩이 보인다.
 * 값을 검사하지 않는다. 잘못된 창은 창을 쓰는 함수가 null 로 거른다.
 */
export function arcWindowFromViewCenter(circumferenceMm: number, viewCenterMm: number): ArcWindow {
  return {
    circumferenceMm,
    startMm: wrapMm(viewCenterMm - circumferenceMm / 4, circumferenceMm),
    lengthMm: circumferenceMm / 2,
  };
}

/** 프레임 모델 B: 면마다 보이는 반둘레 폭을 따로 둔다. 창은 0 부터 시작한다. 값 검사는 모델 A 와 같다. */
export function arcWindowFromHalfCircumference(halfCircumferenceMm: number): ArcWindow {
  return { circumferenceMm: halfCircumferenceMm * 2, startMm: 0, lengthMm: halfCircumferenceMm };
}

/**
 * 원주 위치(mm)를 창 안 비율(0~1)로 바꾼다. 둘레를 넘는 위치는 감아서 본다.
 * 창 밖이거나 숫자가 아니거나 창이 잘못됐으면 null 이다.
 * 창 시작보다 오차만큼 작은 위치는 감겨서 둘레 바로 아래가 되므로 창 시작으로 본다.
 */
export function arcMmToWindowFraction(arcMm: number, arcWindow: ArcWindow): number | null {
  if (!isValidArcWindow(arcWindow) || !Number.isFinite(arcMm)) return null;
  const { circumferenceMm, startMm, lengthMm } = arcWindow;
  const offset = wrapMm(arcMm - startMm, circumferenceMm);
  const fromStart = offset > circumferenceMm - FRACTION_EPSILON * lengthMm ? 0 : offset;
  const fraction = fromStart / lengthMm;
  if (!Number.isFinite(fraction) || fraction > 1 + FRACTION_EPSILON) return null;
  return Math.min(1, fraction);
}

/**
 * 창 안 비율(0~1)을 원주 위치(0 이상 둘레 미만 mm)로 바꾼다.
 * 비율이 숫자가 아니거나 0~1 밖이거나 창이 잘못됐거나 위치 계산이 넘치면 null 이다. 0~1 밖 비율을 뒷면 위치로 감지 않는다.
 */
export function windowFractionToArcMm(fraction: number, arcWindow: ArcWindow): number | null {
  if (
    !isValidArcWindow(arcWindow) ||
    !Number.isFinite(fraction) ||
    fraction < -FRACTION_EPSILON ||
    fraction > 1 + FRACTION_EPSILON
  ) {
    return null;
  }
  const clamped = Math.min(1, Math.max(0, fraction));
  const arcMm = wrapMm(arcWindow.startMm + clamped * arcWindow.lengthMm, arcWindow.circumferenceMm);
  return Number.isFinite(arcMm) ? arcMm : null;
}
